import { memo, useMemo, useState } from "react";
import { CalendarMinus, Hourglass, Percent, UserMinus, UserX, Users } from "lucide-react";

import { usePageBack } from "../../context/PageBackContext.js";
import { cn } from "../../lib/utils.js";
import { reportAnchor, reportRange, shiftReportAnchor, REPORT_PERIOD_OPTIONS, type ReportPeriod } from "../../lib/reportRange.js";
import { SummaryStatSuffix } from "../../components/SummaryStatCard.js";
import { DropdownMultiSelect, DropdownSelect } from "../../components/DropdownButton.js";
import { SearchInput } from "../../components/ui/search-input.js";
import { SpecularButton } from "../../components/ui/specular-button.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";
import type { PaginationMeta } from "../../components/ui/pagination.js";
import {
  ReportPage, FilterBar, ErrorBanner, OrgFilters, ExportButton, CardGrid, ReportStatCard, ReportTableFrame, ReportFooter, PageSizeSelect, TopListTabsCard,
  TableScrollStart, TableToolbar, dropdownProps, splitList, joinList, stickyName, stickyCode, NAME_WIDTH, CODE_WIDTH, type OpenState,
} from "../../components/reports/ReportKit.js";
import {
  useUrlFilters, useDebouncedSearch, useReport, useOrgMeta, toParams, downloadReport,
} from "../../components/reports/useReport.js";

interface LopRow {
  id: string; name: string; code: string; department: string | null; office: string | null;
  workingDays: number; lwpDays: number; lopOnLeave: number; totalLop: number;
  absentDays: number; awaitingDays: number; leaveDays: number; pendingLeaveDays: number; absenteeismPct: number;
}
interface Ranked { id: string; name: string; code: string; days: number }
interface LopReport {
  from: string; to: string;
  rows: LopRow[];
  /** Loaded by a separate request (`part=cards`), so a page turn never refetches it. */
  cards?: {
    employees: number; totalLop: number; lwpDays: number; employeesWithLop: number;
    absentDays: number; awaitingDays: number; avgAbsenteeismPct: number; pendingLeaveDays: number;
    mostLop: Ranked[]; mostAbsent: Ranked[];
  };
  meta: PaginationMeta;
}

const DEFAULTS = {
  search: "", officeIds: "", departmentIds: "", employeeTypes: "", period: "month", anchor: "",
  issues: "", page: "1", limit: "25",
};
// The cards cover everyone in scope; the issue filters narrow only the table.
const ROW_ONLY = ["hasLop", "hasAbsence"];

const API = "/reports/hr/lop";

const ISSUE_OPTIONS = [
  { value: "hasLop", label: "Has LOP" },
  { value: "hasAbsence", label: "Has unauthorised absence" },
];

const TYPE_OPTIONS = [
  { value: "FULL_TIME", label: "Full-time" },
  { value: "PART_TIME", label: "Part-time" },
  { value: "CONTRACTOR", label: "Contractor" },
  { value: "INTERN", label: "Intern" },
];

const NUM = "px-2 text-center tabular-nums";

/** One employee's row. Memoised: 25-100 rows a page, and a page turn rebuilds the list. */
const LopTableRow = memo(function LopTableRow({ row }: { row: LopRow }) {
  return (
    <TableRow className="group hover:bg-surface-50">
      <TableCell className={cn(stickyName, "bg-surface-0 group-hover:bg-surface-50")}>
        <div className={cn(NAME_WIDTH, "truncate font-semibold text-surface-900")} title={[row.name, row.department, row.office].filter(Boolean).join(" · ")}>{row.name}</div>
      </TableCell>
      <TableCell className={cn(stickyCode, "bg-surface-0 text-surface-600 group-hover:bg-surface-50")}>
        <div className={cn(CODE_WIDTH, "truncate")}>{row.code}</div>
      </TableCell>
      <TableCell className="px-3 text-surface-700"><div className="max-w-[160px] truncate">{row.department ?? "—"}</div></TableCell>
      <TableCell className={cn(NUM, "border-l border-surface-200")}>{row.workingDays}</TableCell>
      <TableCell className={cn(NUM, row.lwpDays > 0 && "font-semibold text-[var(--color-warning-fg)]")}>{row.lwpDays}</TableCell>
      <TableCell className={cn(NUM, row.lopOnLeave > 0 && "font-semibold text-[var(--color-warning-fg)]")}>{row.lopOnLeave}</TableCell>
      <TableCell className={cn(NUM, "font-bold", row.totalLop > 0 && "text-[var(--color-warning-fg)]")}>{row.totalLop}</TableCell>
      <TableCell className={cn(NUM, "border-l border-surface-200", row.absentDays > 0 && "font-semibold text-[var(--color-error-fg)]")}>{row.absentDays}</TableCell>
      <TableCell className={NUM} title="Days after the latest biometric upload: not counted as absent">{row.awaitingDays}</TableCell>
      <TableCell className={cn(NUM, "border-l border-surface-200")}>{row.leaveDays}</TableCell>
      <TableCell className={cn(NUM, row.pendingLeaveDays > 0 && "text-[var(--color-warning-fg)]")}>{row.pendingLeaveDays}</TableCell>
      <TableCell
        className={cn(NUM, "border-l border-surface-200 font-semibold", row.absenteeismPct >= 20 && "text-[var(--color-error-fg)]", row.absenteeismPct > 0 && row.absenteeismPct < 20 && "text-[var(--color-warning-fg)]")}
        title="(Total LOP + unauthorised absent) ÷ working days"
      >
        {row.workingDays > 0 ? `${row.absenteeismPct}%` : "—"}
      </TableCell>
    </TableRow>
  );
});

export default function LopReportPage() {
  usePageBack("/dashboard");
  const [f, setF] = useUrlFilters(DEFAULTS);
  const [search, setSearch] = useDebouncedSearch(f.search, (v) => setF({ search: v }));
  const [open, setOpen] = useState<string | null>(null);
  const openState: OpenState = { open, setOpen };
  const org = useOrgMeta();

  const period = (REPORT_PERIOD_OPTIONS.some((o) => o.value === f.period) ? f.period : "month") as ReportPeriod;
  const anchor = f.anchor || reportAnchor(period);
  const range = reportRange(period, anchor);

  const params = useMemo(() => {
    const { period: _p, anchor: _a, issues, ...rest } = f;
    const picked = splitList(issues);
    return toParams(rest, {
      from: range.from, to: range.to,
      ...(picked.includes("hasLop") ? { hasLop: "true" } : {}),
      ...(picked.includes("hasAbsence") ? { hasAbsence: "true" } : {}),
    });
  }, [f, range.from, range.to]);
  const { data, cards: c, loading, error } = useReport<LopReport>(API, params, "LopReportPage", ROW_ONLY);

  const rows = data?.rows ?? [];

  return (
    <ReportPage
      title="LOP & Absenteeism"
      description="Loss-of-pay days from approved leave, kept apart from unauthorised absence, which is never counted into LOP."
    >
      <div className="flex flex-col gap-3">
        <CardGrid>
          <ReportStatCard icon={Users} tone="brand" label="Employees" value={c?.employees ?? "—"} caption={range.label} />
          <ReportStatCard
            icon={CalendarMinus} tone="warning" label="Total LOP"
            value={<>{c?.totalLop ?? "—"}<SummaryStatSuffix> days</SummaryStatSuffix></>}
            caption={c ? `${c.lwpDays} Leave Without Pay` : undefined}
          />
          <ReportStatCard icon={UserMinus} tone="rose" label="With LOP" value={c?.employeesWithLop ?? "—"} caption="Employees with any LOP" />
          <ReportStatCard
            icon={UserX} tone="danger" label="Unauthorised Absent" value={c?.absentDays ?? "—"}
            caption={c?.awaitingDays ? `${c.awaitingDays} days awaiting upload not counted` : "Days, not counted into LOP"}
          />
          <ReportStatCard icon={Percent} tone="accent" label="Avg Absenteeism" value={<>{c?.avgAbsenteeismPct ?? "—"}<SummaryStatSuffix>%</SummaryStatSuffix></>} caption="(LOP + absent) ÷ working days" />
          <ReportStatCard icon={Hourglass} tone="info" label="Pending Leave" value={<>{c?.pendingLeaveDays ?? "—"}<SummaryStatSuffix> days</SummaryStatSuffix></>} caption="Awaiting approval" />
        </CardGrid>
        <TopListTabsCard
          icon={UserMinus} title="Needs attention"
          tabs={[
            {
              value: "lop", label: `Most LOP${c ? ` (${c.mostLop.length})` : ""}`, empty: "No loss of pay in this period.",
              items: (c?.mostLop ?? []).map((t) => ({ key: t.id, primary: t.name, secondary: t.code, value: `${t.days} d` })),
            },
            {
              value: "absent", label: `Most absent${c ? ` (${c.mostAbsent.length})` : ""}`, empty: "No unauthorised absence in this period.",
              items: (c?.mostAbsent ?? []).map((t) => ({ key: t.id, primary: t.name, secondary: t.code, value: `${t.days} d` })),
            },
          ]}
        />
      </div>

      <FilterBar>
        <SearchInput className="w-56" placeholder="Search name or code" value={search} onChange={(e) => setSearch(e.target.value)} />
        <OrgFilters meta={org} officeIds={f.officeIds} departmentIds={f.departmentIds} onChange={setF} openState={openState} />
        <DropdownMultiSelect
          values={splitList(f.employeeTypes)} onChange={(v) => setF({ employeeTypes: joinList(v) })} options={TYPE_OPTIONS}
          allLabel="All types" ariaLabel="Employee type" {...dropdownProps("type", openState)}
        />
        <DropdownSelect
          value={period} onChange={(v) => setF({ period: v, anchor: "" })} options={REPORT_PERIOD_OPTIONS}
          placeholder="Period" ariaLabel="Period" clearable={false} {...dropdownProps("period", openState)}
        />
        <div className="flex items-center gap-3">
          <SpecularButton onClick={() => setF({ anchor: shiftReportAnchor(period, anchor, -1) })} aria-label="Previous period" className="w-8 px-0">&larr;</SpecularButton>
          <div className="min-w-[150px] text-center text-sm font-semibold">{range.label}</div>
          <SpecularButton onClick={() => setF({ anchor: shiftReportAnchor(period, anchor, 1) })} aria-label="Next period" className="w-8 px-0">&rarr;</SpecularButton>
        </div>
        <DropdownMultiSelect
          values={splitList(f.issues)} onChange={(v) => setF({ issues: joinList(v) })} options={ISSUE_OPTIONS}
          allLabel="All employees" ariaLabel="Issues" {...dropdownProps("issues", openState)}
        />
      </FilterBar>

      <TableToolbar>
        <PageSizeSelect limit={f.limit} onLimit={(v) => setF({ limit: v })} openState={openState} />
        <ExportButton onExport={() => downloadReport(API, params, `lop-absenteeism-${range.from}-to-${range.to}.xlsx`)} />
      </TableToolbar>

      <ErrorBanner message={error} />

      <ReportTableFrame loading={loading} empty={rows.length === 0} emptyText={`No employees match these filters for ${range.label}.`}>
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow className="bg-surface-50">
              <TableHead rowSpan={2} className={cn(stickyName, "bg-surface-50 align-bottom")}><div className={NAME_WIDTH}><TableScrollStart />Employee Name</div></TableHead>
              <TableHead rowSpan={2} className={cn(stickyCode, "bg-surface-50 align-bottom")}><div className={CODE_WIDTH}>Emp Code</div></TableHead>
              <TableHead rowSpan={2} className="px-3 align-bottom">Department</TableHead>
              <TableHead rowSpan={2} className="border-l border-surface-200 px-2 text-center align-bottom" title="Weekdays less holidays, days before joining and future days">Working Days</TableHead>
              <TableHead colSpan={3} className="border-l border-surface-200 px-2 text-center">Loss of pay</TableHead>
              <TableHead colSpan={2} className="border-l border-surface-200 px-2 text-center">Attendance</TableHead>
              <TableHead colSpan={2} className="border-l border-surface-200 px-2 text-center">Leave</TableHead>
              <TableHead rowSpan={2} className="border-l border-surface-200 px-2 text-center align-bottom" title="(Total LOP + unauthorised absent) ÷ working days">Absenteeism</TableHead>
            </TableRow>
            <TableRow className="bg-surface-50">
              <TableHead className="border-l border-surface-200 px-2 text-center text-[10px]" title="Leave Without Pay">LWP</TableHead>
              <TableHead className="px-2 text-center text-[10px]" title="The LOP part of other leave types">On Leave</TableHead>
              <TableHead className="px-2 text-center text-[10px]">Total</TableHead>
              <TableHead className="border-l border-surface-200 px-2 text-center text-[10px]" title="Absent with no leave or approval; never counted into LOP">Unauthorised</TableHead>
              <TableHead className="px-2 text-center text-[10px]" title="Days after the latest biometric upload">Awaiting Upload</TableHead>
              <TableHead className="border-l border-surface-200 px-2 text-center text-[10px]">Taken</TableHead>
              <TableHead className="px-2 text-center text-[10px]">Pending</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => <LopTableRow key={r.id} row={r} />)}
          </TableBody>
        </Table>
      </ReportTableFrame>

      <ReportFooter
        meta={data?.meta} onPage={(p) => setF({ page: String(p) })}
      />
    </ReportPage>
  );
}
